import {
  isAdmin,
  hasRole,
  canManageListings,
  canOperateInventory,
  canWorkPurchasing,
  canReadCustomers,
} from "./permissions";

/**
 * Global navigation configuration.
 *
 * Pure and dependency-free except for the permission helpers (which are also
 * pure). This maps the application's capabilities into an operator-facing
 * hierarchy — daily work first, administration/collapsible second — without
 * changing any route, gate, or backend authority.
 *
 * Grouping philosophy (BigCommerce lesson adapted, not copied):
 *   Overview      → "what needs attention?" (status/read surfaces)
 *   Products      → "what am I selling?" (catalogue + listings + stock)
 *   Purchasing    → "what do I need to buy?" (POs + replenishment + suppliers)
 *   Orders        → "what customers bought?" (sales orders + customers + returns)
 *   Fulfilment    → "what needs picking/packing/shipping?"
 *   Administration→ integrations, automation, reconciliation, audit, locations
 */

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  gate?: (role: string | null | undefined) => boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  /** Collapsible groups are progressively disclosed (Administration). */
  collapsible?: boolean;
}

export const navGroups: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: "ri-dashboard-line" },
      { label: "Operational Issues", path: "/exceptions", icon: "ri-error-warning-line" },
      { label: "Reports", path: "/reports", icon: "ri-bar-chart-2-line", gate: (r) => hasRole(r, "manager") },
    ],
  },
  {
    label: "Products",
    items: [
      { label: "Products", path: "/products", icon: "ri-archive-2-line" },
      { label: "Listings", path: "/listings", icon: "ri-store-2-line", gate: canManageListings },
      { label: "Listing Templates", path: "/listing-templates", icon: "ri-layout-masonry-line", gate: canManageListings },
      { label: "Inventory", path: "/inventory", icon: "ri-archive-drawer-line" },
    ],
  },
  {
    label: "Purchasing",
    items: [
      { label: "Purchase Orders", path: "/purchase-orders", icon: "ri-shopping-cart-line", gate: canWorkPurchasing },
      { label: "Restock planning", path: "/replenishment", icon: "ri-shopping-cart-2-line", gate: (r) => hasRole(r, "purchasing", "operations") },
      { label: "Suppliers", path: "/suppliers", icon: "ri-truck-line" },
    ],
  },
  {
    label: "Orders",
    items: [
      { label: "Sales Orders", path: "/sales-orders", icon: "ri-file-list-line" },
      { label: "Customers", path: "/customers", icon: "ri-user-3-line", gate: canReadCustomers },
      { label: "Returns", path: "/returns", icon: "ri-arrow-go-back-line" },
    ],
  },
  {
    label: "Fulfilment",
    items: [
      { label: "Fulfilment", path: "/fulfilment", icon: "ri-inbox-unarchive-line" },
      { label: "Transfers", path: "/transfers", icon: "ri-arrow-left-right-line", gate: canOperateInventory },
      { label: "Stocktake", path: "/stocktake", icon: "ri-list-check-3", gate: canOperateInventory },
    ],
  },
  {
    label: "Administration",
    collapsible: true,
    items: [
      { label: "Integrations", path: "/integrations", icon: "ri-plug-line", gate: isAdmin },
      { label: "Automation Rules", path: "/automation", icon: "ri-robot-2-line", gate: isAdmin },
      { label: "Reconciliation", path: "/reconciliation", icon: "ri-git-pull-request-line", gate: isAdmin },
      { label: "Audit Log", path: "/audit", icon: "ri-time-line", gate: isAdmin },
      { label: "Locations", path: "/locations", icon: "ri-map-pin-line", gate: isAdmin },
    ],
  },
];

export const settingsItem: NavItem = {
  label: "Settings",
  path: "/settings/organization",
  icon: "ri-settings-3-line",
};

/**
 * Settings sub-navigation items (rendered by SettingsNav). Kept here alongside
 * the primary nav model so the "Locations" entry can be asserted to resolve to
 * the single canonical workspace, not a duplicate surface.
 */
export interface SettingsNavItem {
  label: string;
  path: string;
}

export const settingsAdminItems: SettingsNavItem[] = [
  { label: "Organization", path: "/settings/organization" },
  { label: "Team Members", path: "/settings/users" },
  { label: "Locations", path: "/locations" },
  { label: "Roles & Permissions", path: "/settings/roles" },
  { label: "AI Control Centre", path: "/settings/ai" },
];

export const settingsPersonalItems: SettingsNavItem[] = [
  { label: "My Profile", path: "/settings/profile" },
  { label: "Preferences", path: "/settings/preferences" },
];

/**
 * Return the groups that have at least one item visible to the given role.
 * Mirrors the role-aware filtering the sidebar previously did inline, so the
 * visibility rules can be unit-tested without rendering.
 */
export function visibleNavGroups(
  groups: NavGroup[],
  role: string | null | undefined,
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => (item.gate ? item.gate(role) : true)),
    }))
    .filter((group) => group.items.length > 0);
}